import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Label,
  Radio,
  RadioGroup,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
} from '@fluentui/react-components';
import { CustomLabelsEditor } from './side-panel/custom-label-editor';
import { UrlRulesEditor } from './side-panel/url-rules-editor';
import { UrlRulesPreview } from './url-rules-preview';
import { extractTargetsFromViewModel } from './helpers/utils';
import { URL_RULES_OPTIONS, TARGET_TYPE } from './helpers/constants';

const useUrlRulesStyles = makeStyles({
  title: {
    marginBottom: '4px',
  },
  radioGroupContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    '& input[type="radio"]': {
      height: '20px',
      width: '20px',
      ...shorthands.margin('0px'),
    },
    '& .fui-Radio__indicator': {
      height: '20px',
      width: '20px',
      ...shorthands.margin('0px'),
    },
    '& label': {
      ...shorthands.padding('0px', '0px', '0px', '8px'),
    },
  },
  radioOptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    '& .editButton': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      marginLeft: '16px',
      width: 'fit-content',
      color: 'var(--colorCompoundBrandForeground1, #0F6CBD)',
    },
  },
  radioOptionLabelContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
  },
});

export const UrlRulesImpl = (props) => {
  const {
    i18n,
    urlRules,
    urlRulesOption,
    onChange,
    enableSpecialCharactersValidation,
  } = props;
  const styles = useUrlRulesStyles();

  const targets = useMemo(() => extractTargetsFromViewModel(urlRules || []), [urlRules]);
  const {
    urlTargets,
    pageTitleTargets,
    pageContentTargets,
    categoryTargets,
    customLabelTargets,
  } = targets;

  const [isUrlRulesEditorOn, setIsUrlRulesEditorOn] = useState(false);
  const onOpenUrlRulesEditor = useCallback(() => setIsUrlRulesEditorOn(true), []);
  const onCloseUrlRulesEditor = useCallback(() => setIsUrlRulesEditorOn(false), []);
  const [isCustomLabelEditorOn, setIsCustomLabelEditorOn] = useState(false);
  const onOpenCustomLabelEditor = useCallback(() => setIsCustomLabelEditorOn(true), []);
  const onCloseCustomLabelEditor = useCallback(() => setIsCustomLabelEditorOn(false), []);

  const renderTargetAllOption = useCallback(() => (
    <Radio
      className="target-all-option"
      value={URL_RULES_OPTIONS.All}
      label={
        <Text size={300} weight="regular">
          {i18n.getString(_TL_('Target all webpages'))}
        </Text>
      }
    />
  ), [i18n]);

  const renderUrlRulesOption = useCallback(() => (
    <div className={styles.radioOptionContainer}>
      <Radio
        className="url-rules-option"
        value={URL_RULES_OPTIONS.Rules}
        label={
          <div className={styles.radioOptionLabelContainer}>
            <Text size={300} weight="regular">{i18n.getString(_TL_('Specify URL rules to include'))}</Text>
            { urlRulesOption === URL_RULES_OPTIONS.Rules && <Text size={200}>{i18n.getString(_TL_('Send traffic to the most relevant URLs on your site when it’s likely to result in better performance.'))}</Text> }
          </div>
        }
      />
      { urlRulesOption === URL_RULES_OPTIONS.Rules && (urlTargets.length > 0 || pageTitleTargets.length > 0 || pageContentTargets.length > 0 || categoryTargets.length > 0) && (
        <UrlRulesPreview
          urlRules={urlRules}
          i18n={i18n}
          conditions={[TARGET_TYPE.Url, TARGET_TYPE.PageTitle, TARGET_TYPE.PageContent, TARGET_TYPE.Category]}
          titleText={i18n.getString(_TL_('Included URL rules'))}
        />
      )}
      { urlRulesOption === URL_RULES_OPTIONS.Rules && (
        <Button
          className={mergeClasses('editButton', 'url-rules-edit-button')}
          appearance="transparent"
          onClick={onOpenUrlRulesEditor}
        >
          {(urlTargets.length > 0 || pageTitleTargets.length > 0 || pageContentTargets.length > 0 || categoryTargets.length > 0)
            ? i18n.getString(_TL_('Edit URL rules'))
            : i18n.getString(_TL_('Include URL rules'))}
        </Button>
      )}
    </div>
  ), [styles.radioOptionContainer, styles.radioOptionLabelContainer, i18n, urlRulesOption, urlTargets.length, pageTitleTargets.length, pageContentTargets.length, categoryTargets.length, urlRules, onOpenUrlRulesEditor]);

  const renderCustomLabelsOption = useCallback(() => (
    <div className={styles.radioOptionContainer}>
      <Radio
        className="custom-labels-option"
        value={URL_RULES_OPTIONS.Labels}
        label={
          <div className={styles.radioOptionLabelContainer}>
            <Text size={300} weight="regular">{i18n.getString(_TL_('Add custom labels to your URLs'))}</Text>
            { urlRulesOption === URL_RULES_OPTIONS.Labels && <Text size={200}>{i18n.getString(_TL_('Add custom labels from your page feeds to focus your ads on specific categories of pages.'))}</Text> }
          </div>
        }
      />
      { urlRulesOption === URL_RULES_OPTIONS.Labels && customLabelTargets.length > 0 && (
        <UrlRulesPreview
          urlRules={urlRules}
          i18n={i18n}
          conditions={[TARGET_TYPE.CustomLabel]}
          titleText={i18n.getString(_TL_('Custom labels'))}
        />
      )}
      { urlRulesOption === URL_RULES_OPTIONS.Labels && (
        <Button
          className={mergeClasses('editButton', 'custom-labels-edit-button')}
          appearance="transparent"
          onClick={onOpenCustomLabelEditor}
        >
          {(customLabelTargets.length > 0)
            ? i18n.getString(_TL_('Edit custom labels'))
            : i18n.getString(_TL_('Include custom labels'))}
        </Button>
      )}
    </div>
  ), [styles.radioOptionContainer, styles.radioOptionLabelContainer, i18n, urlRulesOption, customLabelTargets.length, urlRules, onOpenCustomLabelEditor]);

  const renderEditor = useCallback(() => {
    if (isUrlRulesEditorOn) {
      return (
        <UrlRulesEditor
          i18n={i18n}
          urlRules={urlRules}
          onChange={onChange}
          isOpen={isUrlRulesEditorOn}
          onClose={onCloseUrlRulesEditor}
          enableSpecialCharactersValidation={enableSpecialCharactersValidation}
        />
      );
    } else if (isCustomLabelEditorOn) {
      return (
        <CustomLabelsEditor
          i18n={i18n}
          urlRules={urlRules}
          onChange={onChange}
          isOpen={isCustomLabelEditorOn}
          onClose={onCloseCustomLabelEditor}
        />
      );
    }
    return null;
  }, [isUrlRulesEditorOn, isCustomLabelEditorOn, i18n, urlRules, onChange, onCloseUrlRulesEditor, onCloseCustomLabelEditor, enableSpecialCharactersValidation]);

  const onRadioChange = useCallback((_, data) => onChange({ UrlRulesOption: data.value }), [onChange]);

  return (
    <div className="url-rules-container">
      <Label className={styles.title} weight="semibold">{i18n.getString(_TL_('URL rules'))}</Label>
      <RadioGroup
        className={mergeClasses(styles.radioGroupContainer, 'url-rules-option-container')}
        value={urlRulesOption}
        onChange={onRadioChange}
      >
        {renderTargetAllOption()}
        {renderUrlRulesOption()}
        {renderCustomLabelsOption()}
        {renderEditor()}
      </RadioGroup>
    </div>
  );
};

UrlRulesImpl.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  urlRules: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number,
    TargetCondition1: PropTypes.string,
    TargetConditionOperator1: PropTypes.string,
    TargetValue1: PropTypes.string,
    TargetCondition2: PropTypes.string,
    TargetConditionOperator2: PropTypes.string,
    TargetValue2: PropTypes.string,
    TargetCondition3: PropTypes.string,
    TargetConditionOperator3: PropTypes.string,
    TargetValue3: PropTypes.string,
  })).isRequired,
  urlRulesOption: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  enableSpecialCharactersValidation: PropTypes.bool,
};

UrlRulesImpl.defaultProps = {
  urlRulesOption: URL_RULES_OPTIONS.All,
  enableSpecialCharactersValidation: false,
};
